import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { AiFillEyeInvisible, AiFillEye } from "react-icons/ai";
import {
  MdOutlineSupervisorAccount,
  MdOutlineAdminPanelSettings,
  MdOutlineMail,
  MdOutlineLock,
} from "react-icons/md";
import { FaArrowLeft } from "react-icons/fa";
import "../styles/Login.css";
import Logo from "../assets/images/TCUK-Logo.png";
import HeaderLogo from "../components/HeaderLogo.jsx";
import { API_URL } from "../constant";

export default function LoginPage({ initialRole }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current role based on prop or current URL path
  const getInitialRole = () => {
    if (initialRole) return initialRole;
    if (location.pathname.includes("it")) return "it";
    return "superadmin";
  };

  const [role, setRole] = useState(getInitialRole());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const current = getInitialRole();
    setRole(current);
    setErrors({});
  }, [location.pathname, initialRole]);

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setErrors({});
    if (newRole === "it") {
      navigate("/it/login");
    } else {
      navigate("/superadmin/login");
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_URL}/superadmin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: email,
          password: password,
          portal: role === "it" ? "it" : "superadmin",
        }),
      });

      const data = await response.json();

      console.log("Login API Response:", {
        status: response.status,
        data: data,
        requires_otp: data.requires_otp,
        user_id: data.user_id,
      });

      if (data.requires_otp === true && data.user_id) {
        console.log("OTP required, navigating to 2FA page");
        localStorage.setItem("superadmin_user_id", data.user_id);
        localStorage.setItem("superadmin_email", email);
        localStorage.setItem("login_role", role);
        localStorage.removeItem("auth_token");
        navigate("/2fa");
        return;
      }

      if (response.ok && data.access_token && data.requires_otp === false) {
        localStorage.setItem("auth_token", data.access_token);
        const userRole = data.role || data.user?.role || (role === "it" ? "it_admin" : "superadmin");
        localStorage.setItem("user_role", userRole);
        if (data.user) {
          localStorage.setItem("user_data", JSON.stringify(data.user));
        }
        if (userRole === "it_admin") {
          navigate("/it-portal/user-topcoin");
        } else {
          navigate("/dashboard");
        }
        return;
      }

      if (data.errors) {
        const validationErrors = {};
        Object.keys(data.errors).forEach((key) => {
          validationErrors[key] = data.errors[key][0];
        });
        setErrors(validationErrors);
      } else if (data.message) {
        switch (data.message) {
          case "Unauthorized access":
            setErrors({ general: "Invalid email or password" });
            break;
          case "Username or Password incorrect":
            setErrors({ general: "Invalid email or password" });
            break;
          case "Email not verified":
            setErrors({
              general: "Email not verified. Please verify your email first.",
            });
            break;
          default:
            setErrors({ general: data.message });
        }
      } else {
        setErrors({ general: "Login failed. Please try again." });
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors({
        general: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isIT = role === "it";

  return (
    <div className="auth-page-wrapper">
      <HeaderLogo title={isIT ? "TCUK IT PORTAL" : "TCUK SADMIN"} />

      <div className="auth-login-card">
        <div className="auth-logo-glow-wrap">
          <img src={Logo} alt="TopCoin UK" className="auth-brand-logo" />
        </div>

        {/* 2-Option Role Switcher Tab */}
        <div className="auth-role-tabs">
          <button
            type="button"
            className={`auth-role-tab ${!isIT ? "active" : ""}`}
            onClick={() => handleRoleChange("superadmin")}
            aria-selected={!isIT}
          >
            <MdOutlineSupervisorAccount size={17} />
            <span>Super Admin</span>
          </button>
          <button
            type="button"
            className={`auth-role-tab ${isIT ? "active" : ""}`}
            onClick={() => handleRoleChange("it")}
            aria-selected={isIT}
          >
            <MdOutlineAdminPanelSettings size={17} />
            <span>IT Admin</span>
          </button>
        </div>

        <h2 className="auth-card-title">
          {isIT ? "IT Portal Login" : "Super Admin Login"}
        </h2>
        <p className="auth-card-subtitle">
          {isIT
            ? "Sign in with your IT Administrator credentials"
            : "Sign in with your Super Admin credentials"}
        </p>

        {errors.general && (
          <div className="auth-general-error">{errors.general}</div>
        )}

        <form onSubmit={handleLogin} className="auth-form" noValidate>
          <div className="auth-field-group">
            <label className="auth-field-label">Email Address</label>
            <div className={`auth-input-container ${errors.email ? "has-error" : ""}`}>
              <MdOutlineMail className="auth-input-icon" size={20} />
              <input
                type="email"
                placeholder={isIT ? "admin.it@topcoin.uk" : "admin@topcoin.uk"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: "" });
                }}
                disabled={isLoading}
                className="auth-text-input"
                maxLength={255}
                autoComplete="email"
              />
            </div>
            {errors.email && (
              <span className="auth-field-error">{errors.email}</span>
            )}
          </div>

          <div className="auth-field-group">
            <label className="auth-field-label">Password</label>
            <div className={`auth-input-container ${errors.password ? "has-error" : ""}`}>
              <MdOutlineLock className="auth-input-icon" size={20} />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: "" });
                }}
                disabled={isLoading}
                className="auth-text-input auth-password-input"
                maxLength={255}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="auth-eye-btn"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <AiFillEye size={20} />
                ) : (
                  <AiFillEyeInvisible size={20} />
                )}
              </button>
            </div>
            {errors.password && (
              <span className="auth-field-error">{errors.password}</span>
            )}
          </div>

          <div className="auth-forgot-row">
            <span
              className="auth-forgot-link"
              onClick={() => !isLoading && navigate("/forgot-password")}
              role="button"
              tabIndex={0}
            >
              Forgot password?
            </span>
          </div>

          <button
            type="submit"
            className={`auth-submit-btn ${isLoading ? "is-loading" : ""}`}
            disabled={isLoading}
          >
            {isLoading
              ? "Authenticating..."
              : isIT
              ? "Login as IT"
              : "Login as Super Admin"}
          </button>
        </form>

        <div className="auth-back-row">
          <Link to="/" className="auth-back-link">
            <FaArrowLeft size={12} />
            <span>Back to Portal Selection</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
